import React from 'react';

import { Transition } from '@headlessui/react';

import type { CollapsiblePanelProps } from './types';
import { cn } from '../../utils/cn';

/**
 * The collapsing content region of a {@link CollapsibleSection}.
 *
 * Renders its children inside an animated, height-collapsing container. It does
 * not depend on Headless UI's `Disclosure` context, so it can be reused on its
 * own wherever a show/hide transition is needed.
 *
 * @param children - The content to be displayed when open.
 * @param className - Extra class names for the animated grid wrapper.
 * @param isOpen - Whether the panel is open or not.
 * @param keepMounted - When true, children stay mounted while closed.
 *
 * @example
 * <CollapsiblePanel isOpen={isOpen}>
 *   <div>Content</div>
 * </CollapsiblePanel>
 */
export const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
    children,
    className,
    isOpen,
    keepMounted = false,
}) => {
    if (keepMounted) {
        return (
            <div
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-300 ease-out',
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    className,
                )}
                inert={!isOpen}
            >
                <div className="overflow-hidden">{children}</div>
            </div>
        );
    }

    return (
        <Transition show={isOpen}>
            <div
                className={cn(
                    'grid grid-rows-[1fr] opacity-100 transition-all duration-300 ease-out',
                    'data-[closed]:grid-rows-[0fr] data-[closed]:opacity-0',
                    className,
                )}
            >
                <div className="overflow-hidden">{children}</div>
            </div>
        </Transition>
    );
};
